class HeaderSearchTypingEffect {
  constructor({ config, headerTarget, inputTarget, searchDialog }) {
    this.config = config;
    this.headerTarget = headerTarget;
    this.inputTarget = inputTarget;
    this.searchDialog = searchDialog;
    this.stopAt = config.stopAt;
    this.prompts = (config.terms || "")
      .split("|")
      .map((term) => term.trim())
      .filter(Boolean)
      .map((term) => `${config.prefix || ""}${term}`);
    this.currentIndex = 0;
    this.isPaused = false;
    this.timeouts = [];
  }

  clearTimeouts() {
    this.timeouts.forEach(clearTimeout);
    this.timeouts = [];
  }

  schedule(callback, delay) {
    const timeoutId = setTimeout(callback, delay);
    this.timeouts.push(timeoutId);
    return timeoutId;
  }

  readText() {
    const target = this.headerTarget || this.inputTarget;
    const element = target?.el;

    if (!element) {
      return "";
    }

    if (target.mode === "attr") {
      return element.getAttribute("data-placeholder") || "";
    }

    return element.placeholder || "";
  }

  setPlaceholder(text) {
    if (this.isPaused) {
      return;
    }

    if (this.headerTarget) {
      if (this.headerTarget.mode === "attr") {
        this.headerTarget.el.setAttribute("data-placeholder", text);
      } else if (!this.headerTarget.el.value) {
        this.headerTarget.el.placeholder = text;
      }
    }

    if (this.inputTarget && this.shouldAnimateInput()) {
      this.inputTarget.el.placeholder = text;
    }
  }

  shouldAnimateInput() {
    return !this.inputTarget?.el.value && !this.headerTarget?.el.value;
  }

  deleteText(callback) {
    if (this.isPaused) {
      callback();
      return;
    }

    const currentText = this.readText();
    let index = currentText.length;

    const deleteNext = () => {
      if (index > this.stopAt) {
        index -= 1;
        this.setPlaceholder(currentText.substring(0, index));
        this.schedule(deleteNext, 80);
      } else {
        callback();
      }
    };

    deleteNext();
  }

  typeText(text, callback) {
    let index = this.stopAt;

    this.setPlaceholder(text.substring(0, this.stopAt));

    const typeNext = () => {
      if (index < text.length) {
        this.setPlaceholder(text.substring(0, index + 1));
        index += 1;
        this.schedule(typeNext, 100);
      } else {
        this.schedule(callback, 2000);
      }
    };

    this.schedule(typeNext, 100);
  }

  startLoop() {
    this.deleteText(() => {
      this.typeText(this.prompts[this.currentIndex], () => {
        this.currentIndex = (this.currentIndex + 1) % this.prompts.length;
        this.startLoop();
      });
    });
  }

  start() {
    if (!this.headerTarget && !this.inputTarget) {
      return;
    }

    if (this.prompts.length === 0) {
      return;
    }

    this.clearTimeouts();
    this.isPaused = false;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.setPlaceholder(this.prompts[this.currentIndex] || this.prompts[0]);
      return;
    }

    this.typeText(this.prompts[this.currentIndex] || this.prompts[0], () => {
      this.currentIndex = (this.currentIndex + 1) % this.prompts.length;
      this.schedule(() => this.startLoop(), 2000);
    });
  }

  pause() {
    this.isPaused = true;
    this.clearTimeouts();
  }

  connect() {
    if (this.prompts.length === 0) {
      return;
    }

    if (!this.headerTarget && !this.inputTarget) {
      return;
    }

    const inputs = [this.inputTarget?.el, this.headerTarget?.mode === "placeholder" ? this.headerTarget.el : null].filter(Boolean);

    inputs.forEach((input) => {
      input.addEventListener("focus", () => this.pause());
      input.addEventListener("input", () => this.pause());
      input.addEventListener("blur", () => {
        if (this.shouldAnimateInput() && this.isPaused) {
          this.start();
        }
      });
    });

    this.searchDialog?.addEventListener("dialog:after-hide", () => {
      if (this.shouldAnimateInput() && this.isPaused) {
        this.start();
      }
    });

    this.start();
  }
}

function getTypingConfig(sectionId) {
  const configElement = document.getElementById(`header-search-typing-config-${sectionId}`);

  if (!configElement) {
    return null;
  }

  try {
    return JSON.parse(configElement.textContent);
  } catch {
    return null;
  }
}

function initHeaderSearchTyping() {
  document.querySelectorAll('[id^="header-search-typing-config-"]').forEach((configElement) => {
    if (configElement.hasAttribute("data-typing-initialized")) {
      return;
    }

    const sectionId = configElement.id.replace("header-search-typing-config-", "");
    const searchDialog = document.getElementById(`header-search-${sectionId}`);
    const config = getTypingConfig(sectionId);

    if (!searchDialog || !config || !config.terms) {
      return;
    }

    configElement.setAttribute("data-typing-initialized", "");

    let headerTarget = null;
    let inputTarget = null;

    searchDialog.querySelectorAll("[data-search-typing]").forEach((input) => {
      inputTarget = { el: input, mode: "placeholder" };
    });

    document.querySelectorAll(".header-search-typing").forEach((root) => {
      const trigger = root.querySelector(`[aria-controls="header-search-${sectionId}"]`);
      const headerInput = root.querySelector(".header-search-typing__input");
      const textElement = headerInput || root.querySelector(".header-search-typing__text");

      if ((trigger || root.getAttribute("data-header-search-section") === sectionId) && textElement) {
        headerTarget = { el: textElement, mode: headerInput ? "placeholder" : "attr" };

        headerInput?.form?.addEventListener("submit", (event) => {
          if (!headerInput.value.trim()) {
            event.preventDefault();
            headerInput.focus();
          }
        });
      }
    });

    if (!headerTarget && !inputTarget) {
      return;
    }

    new HeaderSearchTypingEffect({
      config,
      headerTarget,
      inputTarget,
      searchDialog,
    }).connect();
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initHeaderSearchTyping);
} else {
  initHeaderSearchTyping();
}
